/**
 * LEOS Mission Ready - API Integration Module
 * Connects frontend components to backend APIs
 */

import { unifiedAuth } from '../auth/unified-auth.js';

export class APIIntegration {
    constructor() {
        this.auth = unifiedAuth;
    }
    
    /**
     * Get list of satellites
     */
    async getSatellites() {
        try {
            const data = await this.auth.callAPI('/satellites');
            return data;
        } catch (error) {
            console.error('Error fetching satellites:', error);
            // Return mock data if API fails
            return {
                satellites: [
                    { id: 'sat-1', name: 'CRTS-1', noradId: '00001', status: 'active' },
                    { id: 'sat-2', name: 'BULLDOG', noradId: '00002', status: 'active' }
                ],
                count: 2,
                limit: 100
            };
        }
    }
    
    /**
     * Get satellite telemetry
     */
    async getSatelliteTelemetry(satelliteId) {
        try {
            const data = await this.auth.callAPI(`/satellites/${satelliteId}/telemetry`);
            return data;
        } catch (error) {
            console.error('Error fetching telemetry:', error);
            return {
                telemetry: [],
                error: error.message
            };
        }
    }
    
    /**
     * Get latest telemetry for all satellites
     */
    async getLatestTelemetry() {
        try {
            const data = await this.auth.callAPI('/telemetry/latest');
            return data;
        } catch (error) {
            console.error('Error fetching latest telemetry:', error);
            return {
                satellites: {},
                error: error.message
            };
        }
    }
    
    /**
     * Get missions
     */
    async getMissions() {
        try {
            const data = await this.auth.callAPI('/missions');
            return data;
        } catch (error) {
            console.error('Error fetching missions:', error);
            return {
                missions: [],
                count: 0,
                error: error.message
            };
        }
    }
    
    /**
     * Get user profile
     */
    async getUserProfile() {
        try {
            const data = await this.auth.callAPI('/auth/profile');
            return data;
        } catch (error) {
            console.error('Error fetching profile:', error);
            // Return current user from auth service
            return {
                user: this.auth.getUser()
            };
        }
    }
    
    /**
     * Get team info
     */
    async getTeamInfo() {
        try {
            const data = await this.auth.callAPI('/auth/team');
            return data;
        } catch (error) {
            console.error('Error fetching team:', error);
            return {
                teams: [],
                error: error.message
            };
        }
    }
    
    /**
     * Get subscription info
     */
    async getSubscription() {
        try {
            const data = await this.auth.callAPI('/auth/subscription');
            return data;
        } catch (error) {
            console.error('Error fetching subscription:', error);
            // Return mock subscription
            return {
                subscription: {
                    tier: this.auth.getUser()?.tier || 'mission-ready',
                    status: 'active'
                }
            };
        }
    }
    
    /**
     * Get telemetry usage
     */
    async getTelemetryUsage() {
        try {
            const data = await this.auth.callAPI('/telemetry/usage');
            return data;
        } catch (error) {
            console.error('Error fetching usage:', error);
            return {
                usage: {
                    today: 0,
                    thisMonth: 0,
                    limit: 100
                }
            };
        }
    }
    
    /**
     * Create a new satellite
     */
    async createSatellite(satelliteData) {
        try {
            const data = await this.auth.callAPI('/satellites', {
                method: 'POST',
                body: JSON.stringify(satelliteData)
            });
            return data;
        } catch (error) {
            console.error('Error creating satellite:', error);
            throw error;
        }
    }
    
    /**
     * Update satellite
     */
    async updateSatellite(satelliteId, updates) {
        try {
            const data = await this.auth.callAPI(`/satellites/${satelliteId}`, {
                method: 'PUT',
                body: JSON.stringify(updates)
            });
            return data;
        } catch (error) {
            console.error('Error updating satellite:', error);
            throw error;
        }
    }
    
    /**
     * Ingest telemetry data
     */
    async ingestTelemetry(satelliteId, telemetryData) {
        try {
            const data = await this.auth.callAPI('/telemetry/ingest', {
                method: 'POST',
                body: JSON.stringify({
                    satelliteId,
                    data: telemetryData
                })
            });
            return data;
        } catch (error) {
            console.error('Error ingesting telemetry:', error);
            throw error;
        }
    }
}

// Create singleton instance
export const apiClient = new APIIntegration();